import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { getNav } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const FALLBACK_NAV = [
  { id: "new", label: "New", to: "/collection/new-at-lemce", mega_enabled: false, mega_links: [] },
  {
    id: "men", label: "Men", to: "/collection/mens-innerwear", mega_enabled: true,
    mega_caption: "Seamless Comfort. Lasting Shape.",
    mega_image: "https://images.pexels.com/photos/8874933/pexels-photo-8874933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=520",
    mega_links: [
      { label: "Modal Boxer Briefs", to: "/collection/mens-innerwear" },
      { label: "Everyday Trunks", to: "/collection/mens-innerwear" },
      { label: "All Innerwear", to: "/collection/mens-innerwear" },
    ],
  },
  {
    id: "winter", label: "Winter", to: "/collection/winter-comfort", mega_enabled: true,
    mega_caption: "Warmth That Goes With You.",
    mega_image: "https://images.pexels.com/photos/19847117/pexels-photo-19847117.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=520",
    mega_links: [
      { label: "Winter Socks", to: "/collection/winter-socks" },
      { label: "Winter Footwear", to: "/collection/winter-footwear" },
      { label: "Lounge & Cosy", to: "/collection/winter-comfort" },
    ],
  },
  { id: "best", label: "Best Sellers", to: "/collection/best-sellers", mega_enabled: false, mega_links: [] },
  { id: "about", label: "About LEMCE", to: "/about", mega_enabled: false, mega_links: [] },
];

export const Header = () => {
  const { cartCount, wishlist, setCartOpen, setSearchOpen } = useStore();
  const { user } = useAuth();
  const [compact, setCompact] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [nav, setNav] = useState(FALLBACK_NAV);
  const navigate = useNavigate();

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        setCompact((c) => (c ? y > 20 : y > 60)); // hysteresis: shrink past 60, expand only below 20
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    getNav().then((d) => { if (Array.isArray(d) && d.length) setNav(d); }).catch(() => {});
  }, []);

  const megaTab = nav.find((t) => t.id === hovered && t.mega_enabled && (t.mega_links || []).length);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-lemce-matte text-lemce-ivory text-[11px] py-2 text-center tracking-widest uppercase">
        Complimentary Shipping Above ₹1299 · Considered Comfort, Delivered
      </div>

      <div
        className="bg-lemce-ivory/95 backdrop-blur-xl border-b border-lemce-beige/60"
        onMouseLeave={() => setHovered(null)}
      >
        <div className={`max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center gap-4 transition-all duration-300 ${compact ? "py-3" : "py-5"}`}>
          {/* LEFT: mobile menu + desktop nav */}
          <div className="flex-1 flex items-center gap-5 xl:gap-7 min-w-0">
            <button className="lg:hidden shrink-0 p-2.5 -ml-2.5 flex items-center justify-center" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
              {nav.map((n) => (
                <Link
                  key={n.id}
                  to={n.to}
                  onMouseEnter={() => setHovered(n.mega_enabled && (n.mega_links || []).length ? n.id : null)}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[12px] tracking-widest uppercase text-lemce-charcoal hover:text-lemce-matte transition-colors relative py-1 whitespace-nowrap"
                >
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* CENTER: logo */}
          <Link to="/" className="shrink-0 flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_40bb7f7e-ca28-4c7b-9f37-39e4bdb4b2a7/artifacts/olw036ov_lemce%20logo%20ff.webp"
              alt="LEMCE"
              className={`w-auto object-contain transition-all duration-300 ${compact ? "h-7" : "h-9"}`}
            />
          </Link>

          {/* RIGHT: icons */}
          <div className="flex-1 flex items-center justify-end gap-1 sm:gap-2">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="header-search-btn" className="p-2.5 flex items-center justify-center">
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate(user ? "/account" : "/login")} aria-label="Account" data-testid="header-account-btn" className="hidden sm:flex p-2.5 items-center justify-center">
              <User className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate("/wishlist")} aria-label="Wishlist" data-testid="header-wishlist-btn" className="relative p-2.5 flex items-center justify-center">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-lemce-matte text-lemce-ivory text-[9px] w-4 h-4 flex items-center justify-center rounded-full">{wishlist.length}</span>
              )}
            </button>
            <button onClick={() => setCartOpen(true)} aria-label="Cart" data-testid="header-cart-btn" className="relative p-2.5 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-lemce-matte text-lemce-ivory text-[9px] w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">{cartCount}</span>
              )}
            </button>
          </div>
        </div>

        {megaTab && (
          <div
            className="hidden lg:block absolute left-0 right-0 bg-lemce-ivory border-b border-lemce-beige shadow-sm"
            onMouseEnter={() => setHovered(megaTab.id)}
            data-testid={`mega-menu-${megaTab.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="max-w-[1400px] mx-auto px-12 py-10 grid grid-cols-3 gap-10">
              <div className="col-span-1">
                <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-5">{megaTab.label}</p>
                <ul className="space-y-3">
                  {megaTab.mega_links.map((l, i) => (
                    <li key={i}>
                      <Link to={l.to} onClick={() => setHovered(null)} className="text-sm text-lemce-charcoal hover:text-lemce-matte hover:pl-1 transition-all inline-block">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {megaTab.mega_image && (
                <Link to={megaTab.mega_links[0]?.to || megaTab.to} onClick={() => setHovered(null)} className="col-span-2 relative aspect-[16/7] overflow-hidden group">
                  <img src={megaTab.mega_image} alt={megaTab.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-lemce-matte/25" />
                  {megaTab.mega_caption && <span className="absolute bottom-6 left-6 font-serif text-lemce-ivory text-2xl">{megaTab.mega_caption}</span>}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 bg-lemce-ivory p-0" data-testid="mobile-nav">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Browse LEMCE collections and your account.</SheetDescription>
          <div className="flex items-center justify-between px-6 py-5 border-b border-lemce-beige">
            <span className="font-serif tracking-[0.3em] text-xl">LEMCE</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close"><X className="w-5 h-5" strokeWidth={1.5} /></button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {nav.map((n) => {
              const subs = n.mega_enabled ? (n.mega_links || []) : [];
              if (!subs.length) {
                return (
                  <Link key={n.id} to={n.to} onClick={() => setMobileOpen(false)}
                    className="py-3 text-sm tracking-widest uppercase border-b border-lemce-beige/60">{n.label}</Link>
                );
              }
              const open = mobileExpanded === n.id;
              return (
                <div key={n.id} className="border-b border-lemce-beige/60">
                  <button type="button" onClick={() => setMobileExpanded(open ? null : n.id)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full flex items-center justify-between py-3 text-sm tracking-widest uppercase">
                    {n.label}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
                  </button>
                  {open && (
                    <div className="pb-3 pl-4 flex flex-col" data-testid={`mobile-subnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {subs.map((l, i) => (
                        <Link key={i} to={l.to} onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                          className="py-2.5 text-[13px] tracking-wider uppercase text-lemce-grey">{l.label}</Link>
                      ))}
                      <Link to={n.to} onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                        className="py-2.5 text-[13px] tracking-wider uppercase text-lemce-matte">View all {n.label}</Link>
                    </div>
                  )}
                </div>
              );
            })}
            <Link to="/account" onClick={() => setMobileOpen(false)} className="py-3 text-sm tracking-widest uppercase border-b border-lemce-beige/60">Account</Link>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
